import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import ProfilePreview from '@/components/public-profile/ProfilePreview'
import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_URL || ''

interface PublicProfile {
  username: string
  name: string
  avatar: string | null
  heroImageUrl: string | null
  classYear: string | null
  school: string | null
  sports: string[]
  socialAccounts: {
    platform: string
    username: string
    url: string
    followerCount: number
  }[]
  socialChannels?: {
    platform: string
    platformKey?: string
    handle: string | null
    url: string | null
    verified: boolean
    followerCount?: number | null
  }[]
  links: {
    campaigns: any[]
    links: any[]
    partners: any[]
    custom: any[]
  }
}

const PublicProfileView = () => {
  const { username } = useParams<{ username: string }>()
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) return

      try {
        // Use direct axios call since this is a public endpoint (no auth cookies needed)
        // Adjust endpoint path to match backend public implementation
        const response = await axios.get(`${API_BASE}/api/public/p/${username}`)

        if (response.data.success) {
          setProfile(response.data.data)
        } else {
          setError('Profile not found')
        }
      } catch (err: any) {
        console.error('Error fetching public profile:', err)
        setError(err.response?.data?.error || 'Profile not found')
      } finally {
        setIsLoading(false)
      }
    }

    fetchProfile()
  }, [username])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C0FD72] animate-spin" />
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-4">
        <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4">
          <span className="text-2xl">?</span>
        </div>
        <h1 className="text-xl font-bold mb-2">Profile Not Found</h1>
        <p className="text-white/60 text-center max-w-sm">
          The public profile you're looking for doesn't exist or hasn't been published yet.
        </p>
      </div>
    )
  }

  // Map public profile data to the format expected by ProfilePreview
  const normalizedSocialChannels =
    profile.socialChannels && profile.socialChannels.length > 0
      ? profile.socialChannels
      : (profile.socialAccounts || []).map((account) => ({
          platform: account.platform,
          handle: account.username || null,
          url: account.url || null,
          verified: true,
          followerCount: account.followerCount ?? null,
        }))

  const previewProfile = {
    id: 'public',
    username: profile.username,
    name: profile.name,
    heroImageUrl: profile.heroImageUrl,
    classYear: profile.classYear,
    school: profile.school,
    isPublished: true,
    publicUrl: null,
    links: profile.links,
    socialChannels: normalizedSocialChannels,
    // Add missing required properties
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  const previewAthlete = {
    name: profile.name,
    avatar: profile.avatar || undefined,
    school: profile.school ? { name: profile.school } : undefined,
    // sports is string[] in public profile and ProfilePreview can handle strings
    sports: profile.sports,
  }

  return (
    <>
      <ProfilePreview profile={previewProfile} athlete={previewAthlete} isFullPage={true} />
    </>
  )
}

export default PublicProfileView
